import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '../base-classes/base.component';
import { UserPasswordDto } from '@whetstoneeducation/hero-common';
import { Subscription } from 'rxjs';
import * as zxcvbn from 'zxcvbn';
import { AppToastManagerService } from '../services/toast-manager.service';
import { validate } from 'class-validator';
import {
  displayFormErrors,
  dtoToFormGroup,
  getValue,
  updateFormErrors,
  validateAndGetValue
} from '../validation/validation.util';

@Component({
  selector: 'app-password-dialog',
  templateUrl: './password-dialog.template.html',
  styleUrls: ['./password-dialog.scss']
})
export class AppPasswordDialogComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  private static requiredCurrentPasswordValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = (control.value ?? '').toString().trim();
    return value.length ? null : { required: 'Current password is required' };
  }

  public form: FormGroup;
  public passwordDto: UserPasswordDto;
  public email: string;
  public firstName: string;
  public lastName: string;
  public valueChangesSubscription: Subscription;
  public passwordStrength: number;
  public hidePassword: boolean = true;
  public requireCurrentPassword = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AppPasswordDialogComponent>,
    private fb: FormBuilder,
    private toastService: AppToastManagerService
  ) {
    super();
    this.email = data.email;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.requireCurrentPassword = !!data?.requireCurrentPassword;
    this.passwordDto = new UserPasswordDto();
    this.form = dtoToFormGroup(this.passwordDto, this.fb);

    if (this.requireCurrentPassword) {
      if (!this.form.contains('currentPassword')) {
        this.form.addControl(
          'currentPassword',
          new FormControl('', [
            AppPasswordDialogComponent.requiredCurrentPasswordValidator
          ])
        );
      } else {
        this.form
          .get('currentPassword')
          ?.setValidators([
            AppPasswordDialogComponent.requiredCurrentPasswordValidator
          ]);
        this.form.get('currentPassword')?.updateValueAndValidity();
      }
    }
  }

  ngAfterViewInit(): void {
    this.valueChangesSubscription = this.form.valueChanges.subscribe(
      async () => {
        this.passwordDto = await validateAndGetValue<UserPasswordDto>(
          this.form,
          UserPasswordDto
        );
      }
    );
  }

  async checkPasswordStrength(event: any) {
    // Get the current value from the event
    let password = event.target.value;
    let userInputs = [this.email, this.firstName, this.lastName];
    if (!password) password = '';
    let result = zxcvbn(password, userInputs);
    this.passwordStrength = result.score;
  }

  getProgressBarColor() {
    if (this.passwordStrength <= 2) {
      return 'error';
    } else if (this.passwordStrength === 3) {
      return 'warn';
    } else {
      return 'success';
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

  async save(): Promise<void> {
    this.form.markAllAsTouched();

    const password = (this.form.get('password')?.value ?? '').trim();
    const passwordConfirm = (this.form.get('passwordConfirm')?.value ?? '').trim();

    if (!password || !passwordConfirm) {
      if (!password) {
        this.form.get('password')?.setErrors({
          required: 'Password is required.'
        });
      }
      if (!passwordConfirm) {
        this.form.get('passwordConfirm')?.setErrors({
          required: 'Password confirmation is required.'
        });
      }
      this.toastService.error(
        'Please correct the errors on the form before saving.'
      );
      return;
    }

    const dto = await getValue<UserPasswordDto>(this.form, UserPasswordDto);
    const errors = await validate(dto);
    updateFormErrors(errors, this.form);

    if (this.form.invalid) {
      displayFormErrors(this.form);
      this.toastService.error(
        'Please correct the errors on the form before saving.'
      );
      return;
    }

    this.dialogRef.close(dto);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
